import React, { useEffect, useState } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import CountUp from 'react-countup';
import { FiChevronDown, FiArrowRight, FiCheck } from 'react-icons/fi';
import { BiParty, BiCamera, BiMusic } from 'react-icons/bi';
import { Io5Wine, IoDiamondOutline } from 'react-icons/io5';
import { HiBuildingOffice2, HiUserGroup, HiGlobeAlt, HiLightBulb, HiKey, HiWifi, HiVideoCamera, HiSparkles } from 'react-icons/hi2';
import { FaCar, FaSnowflake, FaBolt, FaBed, FaShieldAlt, FaWifi, FaWheelchair, FaArrowUp } from 'react-icons/fa';
import './Home.css';

// Reusable animated section wrapper
const AnimatedSection = ({ children, className = '' }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const Home = () => {
  const { scrollY } = useScroll();
  const y1 = useTransform(scrollY, [0, 1000], [0, 200]);
  const opacity = useTransform(scrollY, [0, 500], [1, 0]);

  // Image URLs (Unsplash)
  const heroImg = "/assets/main1.png";
  const entranceImg = "/assets/main26.png";
  const tradImg = "/assets/main8.png";
  const recImg = "/assets/main4.png";
  const engImg = "/assets/main5.png";
  const stageImg = "/assets/main11.png";
  const diningImg = "https://images.unsplash.com/photo-1555244162-803834f70033?q=80&w=1000&auto=format&fit=crop";
  const suiteImg = "https://images.unsplash.com/photo-1578683010236-d716f9a3f461?q=80&w=1000&auto=format&fit=crop";
  const suiteImg2 = "https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?q=80&w=1000&auto=format&fit=crop";
  const lightImg1 = "/assets/main14.png";
  const lightImg2 = "/assets/main16.png";
  const lightImg3 = "/assets/main15.png";
  const nightImg = "https://images.unsplash.com/photo-1514933651103-005eec06c04b?q=80&w=2000&auto=format&fit=crop";

  return (
    <div className="home-container">
      {/* 1. Hero Section */}
      <section className="hero-section">
        <motion.div className="hero-bg" style={{ y: y1, backgroundImage: `url(${heroImg})` }} />
        <div className="hero-overlay"></div>
        <motion.div className="hero-content" style={{ opacity }}>
          {/* <h2 className="hero-subtitle">Kalyana Mandapam & Auditorium — Kalyana Mandapam</h2> */}
          <h1 className="hero-title">Prasannalakshmi Kalyana Mandapam & Auditorium</h1>
          <p className="hero-tagline">Where Dreams Meet Grandeur</p>
          <button className="btn-primary hero-btn">Book Your Date</button>
        </motion.div>
        <motion.div 
          className="scroll-indicator"
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 1.5 }}
          style={{ opacity }}
        >
          <FiChevronDown />
        </motion.div>
      </section>

      {/* 2. Grand Entrance Reveal */}
      <section className="entrance-section container">
        <AnimatedSection className="split-layout">
          <div className="split-img">
            <img src={entranceImg} alt="Grand Entrance" />
            <div className="img-backdrop"></div>
          </div>
          <div className="split-text">
            <span className="section-subtitle">A Majestic Welcome</span>
            <h2 className="section-title">Welcome to Prasannalakshmi Kalyana Mandapam & Auditorium</h2>
            <p>
              Palakkad's premier Kalyana Mandapam & Auditorium situated at Kanjikode, offering an unparalleled setting for your most cherished moments. 
              With a sprawling layout designed for luxury and comfort, Prasannalakshmi Kalyana Mandapam & Auditorium is where elegance naturally meets modern convenience.
            </p>
            <p>
              Boasting a capacity of 1000+ guests, our venue features majestic architecture, opulent interiors, and meticulous attention to detail, 
              ensuring that every event hosted here becomes a legacy of memories.
            </p>
            <div className="features-list">
              <div className="feature-item"><FiCheck /> Fully Air-Conditioned</div>
              <div className="feature-item"><FiCheck /> Premium Location</div>
              <div className="feature-item"><FiCheck /> Dedicated Event Managers</div>
            </div>
            <button className="btn-outline">Explore Venue <FiArrowRight /></button>
          </div>
        </AnimatedSection>
      </section>

      {/* 3. Host Your Dream Wedding Showcase */}
      <section className="wedding-showcase container">
        <AnimatedSection className="text-center mb-5">
          <span className="section-subtitle">Celebrations</span>
          <h2 className="section-title">Host Your Dream Wedding</h2>
          <p className="section-desc">Experience the pinnacle of luxury with our tailored wedding spaces designed for every ceremony.</p>
        </AnimatedSection>
        
        <div className="cards-grid">
          <AnimatedSection className="showcase-card">
            <div className="card-img-wrap">
              <img src={tradImg} alt="Traditional Ceremonies" />
            </div>
            <div className="card-content">
              <h3>Traditional Ceremonies</h3>
              <p>Authentic spaces designed for traditional rituals with sacred ambiance and modern comforts.</p>
              <a href="#" className="link-arrow">Learn More <FiArrowRight /></a>
            </div>
          </AnimatedSection>

          <AnimatedSection className="showcase-card">
            <div className="card-img-wrap">
              <img src={recImg} alt="Grand Receptions" />
            </div>
            <div className="card-content">
              <h3>Grand Receptions</h3>
              <p>Host awe-inspiring evening receptions in our majestic main hall with state-of-the-art lighting.</p>
              <a href="#" className="link-arrow">Learn More <FiArrowRight /></a>
            </div>
          </AnimatedSection>

          <AnimatedSection className="showcase-card">
            <div className="card-img-wrap">
              <img src={engImg} alt="Intimate Engagements" />
            </div>
            <div className="card-content">
              <h3>Intimate Engagements</h3>
              <p>Cozy yet luxurious settings perfect for smaller gatherings and private celebrations.</p>
              <a href="#" className="link-arrow">Learn More <FiArrowRight /></a>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* 4. Multi-layered Event Experiences */}
      <section className="event-experiences bg-warm">
        <div className="container">
          <AnimatedSection className="text-center mb-5">
            <span className="section-subtitle">Versatility</span>
            <h2 className="section-title">Multi-layered Event Experiences</h2>
          </AnimatedSection>

          <div className="experience-cards">
            {[
              { icon: <BiParty />, title: "Weddings", desc: "Grand celebrations" },
              { icon: <HiBuildingOffice2 />, title: "Corporate Events", desc: "Professional summits" },
              { icon: <BiCamera />, title: "Birthday Parties", desc: "Joyous milestones" },
              { icon: <BiMusic />, title: "Cultural Programs", desc: "Artistic expressions" },
              { icon: <HiSparkles />, title: "Award Ceremonies", desc: "Prestigious galas" },
              { icon: <HiUserGroup />, title: "Family Gatherings", desc: "Memorable reunions" }
            ].map((exp, idx) => (
              <AnimatedSection key={idx} className="exp-card">
                <div className="exp-icon">{exp.icon}</div>
                <h4>{exp.title}</h4>
                <p>{exp.desc}</p>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* 5. Royal Stage Presentation */}
      <section className="stage-parallax" style={{ backgroundImage: `url(${stageImg})` }}>
        <div className="parallax-overlay"></div>
        <AnimatedSection className="stage-content">
          <h2 className="parallax-title">The Royal Stage</h2>
          <p className="parallax-desc">
            A magnificent focal point for your events, featuring expansive dimensions, 
            premium acoustic treatments, and customizable LED backdrops. Let your moments shine.
          </p>
        </AnimatedSection>
      </section>

      {/* 6. Premium Dining Experience */}
      <section className="dining-section container">
        <AnimatedSection className="split-layout reverse">
          <div className="split-text">
            <span className="section-subtitle">Gastronomy</span>
            <h2 className="section-title">Premium Dining Experience</h2>
            <p>
              Our expansive dining hall is designed to accommodate hundreds of guests simultaneously without compromising on comfort or elegance.
            </p>
            <p>
              Whether it's a traditional Sadya or an expansive global buffet, the dedicated dining space is equipped with modern hygienic serving stations, 
              ample seating, and beautiful ambiance to make every meal a celebration in itself.
            </p>
            <ul className="list-styled">
              <li>Spacious Seating Capacity</li>
              <li>Hygienic Modern Kitchen</li>
              <li>Separate Buffet Counters</li>
              <li>Premium Crockery & Cutlery</li>
            </ul>
          </div>
          <div className="split-img">
            <img src={diningImg} alt="Dining Hall" />
            <div className="img-backdrop left"></div>
          </div>
        </AnimatedSection>
      </section>

      {/* 7. Spacious Parking & Guest Facilities */}
      <section className="facilities-section bg-warm">
        <div className="container">
          <AnimatedSection className="text-center mb-5">
            <h2 className="section-title">Guest Facilities & Amenities</h2>
            <p className="section-desc">Comprehensive amenities designed for supreme comfort and convenience.</p>
          </AnimatedSection>
          
          <div className="facilities-grid">
            {[
              { icon: <FaCar />, title: "Valet Parking", desc: "500+ car capacity" },
              { icon: <FaSnowflake />, title: "AC Interiors", desc: "Centralized climate control" },
              { icon: <FaBolt />, title: "Power Backup", desc: "100% generator backup" },
              { icon: <FaBed />, title: "Guest Rooms", desc: "Luxurious accommodation" },
              { icon: <FaShieldAlt />, title: "Security", desc: "24/7 CCTV & Guards" },
              { icon: <FaWifi />, title: "Wi-Fi", desc: "High-speed internet" },
              { icon: <FaWheelchair />, title: "Accessibility", desc: "Wheelchair friendly" },
              { icon: <FaArrowUp />, title: "Elevator Access", desc: "Modern lifts for all floors" }
            ].map((fac, idx) => (
              <AnimatedSection key={idx} className="facility-card">
                <div className="fac-icon">{fac.icon}</div>
                <h4>{fac.title}</h4>
                <p>{fac.desc}</p>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* 8. Bride & Groom Suites */}
      <section className="suites-section container">
        <AnimatedSection className="text-center mb-5">
          <span className="section-subtitle">Comfort & Privacy</span>
          <h2 className="section-title">Bride & Groom Suites</h2>
          <p className="section-desc">Exquisite private quarters for preparation and relaxation before your big moment.</p>
        </AnimatedSection>
        
        <div className="suites-grid">
          <AnimatedSection className="suite-card">
            <img src={suiteImg} alt="Bride Suite" />
            <div className="suite-info">
              <h3>The Bridal Suite</h3>
              <p>Luxurious vanity spaces, full-length mirrors, and plush seating for the ultimate preparation experience.</p>
            </div>
          </AnimatedSection>
          <AnimatedSection className="suite-card">
            <img src={suiteImg2} alt="Groom Suite" />
            <div className="suite-info">
              <h3>The Groom's Quarters</h3>
              <p>Elegant and comfortable relaxing areas with modern amenities and attached private dressing rooms.</p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* 9. Decorative Lighting Showcase */}
      <section className="lighting-showcase">
        <div className="container">
          <AnimatedSection className="text-center mb-5">
            <h2 className="section-title">Spectacular Lighting</h2>
          </AnimatedSection>
          <div className="gallery-grid">
            <AnimatedSection className="gallery-item"><img src={lightImg1} alt="Lighting 1" /></AnimatedSection>
            <AnimatedSection className="gallery-item"><img src={lightImg2} alt="Lighting 2" /></AnimatedSection>
            <AnimatedSection className="gallery-item"><img src={lightImg3} alt="Lighting 3" /></AnimatedSection>
          </div>
        </div>
      </section>

      {/* 10. Beautiful Night-time Ambience */}
      <section className="night-ambience" style={{ backgroundImage: `url(${nightImg})` }}>
        <div className="dark-overlay"></div>
        <AnimatedSection className="night-content">
          <h2 className="golden-text">A Night to Remember</h2>
          <p>As the sun sets, Prasannalakshmi Kalyana Mandapam & Auditorium transforms into a mesmerizing palace of lights, creating an enchanting backdrop for your evening celebrations.</p>
        </AnimatedSection>
      </section>

      {/* 11. Customer Celebration Highlights */}
      <section className="testimonials-section container bg-warm">
        <AnimatedSection className="text-center mb-5">
          <span className="section-subtitle">Testimonials</span>
          <h2 className="section-title">Celebration Highlights</h2>
        </AnimatedSection>
        
        <div className="testimonials-grid">
          {[
            { name: "Rahul & Sneha", event: "Wedding Ceremony", text: "Prasannalakshmi Kalyana Mandapam & Auditorium made our wedding truly magical. The grand stage and lighting were breathtaking. The staff was incredibly supportive." },
            { name: "TechCorp India", event: "Annual Corporate Summit", text: "Excellent facilities for our corporate event. The dining arrangements and acoustics were top-notch. Highly recommend for large gatherings." },
            { name: "Menon Family", event: "Golden Anniversary", text: "A beautiful venue that provided the perfect traditional ambiance mixed with modern luxury. The guest suites were very comfortable." }
          ].map((test, idx) => (
            <AnimatedSection key={idx} className="testimonial-card">
              <div className="quote-icon">"</div>
              <p className="test-text">{test.text}</p>
              <div className="test-author">
                <h4>{test.name}</h4>
                <span>{test.event}</span>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </section>

      {/* 12. Stats Counter */}
      <section className="stats-section">
        <div className="container">
          <div className="stats-grid">
            <AnimatedSection className="stat-item">
              <h3 className="stat-number">
                <CountUp end={500} duration={3} suffix="+" enableScrollSpy />
              </h3>
              <p>Events Hosted</p>
            </AnimatedSection>
            <AnimatedSection className="stat-item">
              <h3 className="stat-number">
                <CountUp end={1000} duration={3} suffix="+" enableScrollSpy />
              </h3>
              <p>Guest Capacity</p>
            </AnimatedSection>
            <AnimatedSection className="stat-item">
              <h3 className="stat-number">
                <CountUp end={50} duration={3} suffix="k+" enableScrollSpy />
              </h3>
              <p>Sq.Ft Area</p>
            </AnimatedSection>
            <AnimatedSection className="stat-item">
              <h3 className="stat-number">
                <CountUp end={15} duration={3} suffix="+" enableScrollSpy />
              </h3>
              <p>Years of Excellence</p>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* 13. Final Booking CTA */}
      <section className="cta-section">
        <AnimatedSection className="cta-content text-center">
          <h2>Ready to Create Unforgettable Memories?</h2>
          <p>Book a tour of Prasannalakshmi Kalyana Mandapam & Auditorium and let us help you plan the perfect event.</p>
          <div className="cta-buttons">
            <button className="btn-primary">Book Your Date</button>
            <button className="btn-outline-light">Contact Us</button>
          </div>
        </AnimatedSection>
      </section>
    </div>
  );
};

export default Home;